import { fieldRegistry } from '../../../core/fields/registry/FieldRegistry';
import { getFieldSection } from './field-sections.config';
import { FieldManifest, FieldType } from '../../../core/fields/types';
import type { TFunction } from 'i18next';

/**
 * Maps field pack field types to manifest property kinds
 */
function mapFieldTypeToKind(fieldType: FieldType): string {
  switch (fieldType) {
    case FieldType.TEXT:
      return 'string';
    case FieldType.NUMBER:
      return 'number';
    case FieldType.BOOLEAN:
      return 'boolean';
    case FieldType.SELECT:
      return 'enum';
    case FieldType.MULTI_SELECT:
      return 'tags';
    case FieldType.DATE:
      return 'date';
    case FieldType.RICH_TEXT:
      return 'richtext';
    case FieldType.COMPOSITE:
      return 'composite';
    default:
      return 'string';
  }
}

/**
 * Maps field pack sections to manifest UI groups
 * This creates a mapping from our new taxonomy to the manifest's group structure
 */
const SECTION_TO_GROUP_MAP: Record<string, string> = {
  identity: 'identity',
  'medical': 'medical',
  'mental-health': 'medical',  // Mental health goes under medical group
  'genetic': 'medical',        // Genetic goes under medical group
  'legal': 'legal',
  'financial': 'financial',
  'social': 'demographics',    // Social goes under demographics
  'professional': 'demographics', // Professional (education/employment) goes under demographics
  'relationships': 'relationships',
  'research': 'research',
  'contact': 'context',        // Contact goes under context
  'notes': 'extras',           // Notes go under extras
};

/**
 * Determines the UI control type based on field configuration
 */
function determineControl(field: FieldManifest): string {
  if (field.type === FieldType.SELECT) {
    if (field.options && field.options.length === 3) {
      return 'toggle-group';
    }
    return 'select';
  }

  if (field.type === FieldType.BOOLEAN) {
    return 'switch';
  }

  if (field.type === FieldType.MULTI_SELECT || field.id.toLowerCase().includes('tag')) {
    return 'tags';
  }

  if (field.type === FieldType.RICH_TEXT || field.multiline) {
    return 'textarea';
  }

  if (field.type === FieldType.DATE) {
    return 'date';
  }

  return 'text';
}

/**
 * Translates a field string if it's a translation key, otherwise returns the string as-is
 */
function translateFieldString(t: TFunction | null, str: string): string {
  if (!t || !str.startsWith('fields.')) {
    return str;
  }
  return t(str, str); // Use the key as fallback
}

/**
 * Converts field pack fields to manifest properties format with translation support
 */
export function generatePropertiesFromFieldPacks(fieldPackIds: string[], t?: TFunction): Array<any> {
  const fields = fieldRegistry.getFieldsFromPacks(fieldPackIds);
  const properties: Array<any> = [];

  for (const field of fields) {
    // Normalize tags to string array
    const tags = Array.isArray(field.tags)
      ? field.tags.map(tag => typeof tag === 'string' ? tag : tag.id)
      : [];

    const section = getFieldSection(tags);
    if (!section) continue;

    const group = SECTION_TO_GROUP_MAP[section.id] || 'extras';
    const kind = mapFieldTypeToKind(field.type);
    const control = determineControl(field);

    const property: any = {
      key: field.id,
      label: translateFieldString(t || null, field.label),
      kind,
      ui: {
        group,
        priority: field.priority || 50,
        width: 'full',
        control,
      },
    };

    // Add enum values/options for SELECT & MULTI_SELECT fields
    if (field.type === FieldType.SELECT && field.options) {
      property.enumValues = field.options.map(opt => opt.value);
      property.options = field.options.map(opt => ({
        ...opt,
        label: translateFieldString(t || null, opt.label)
      }));
    }

    if (field.type === FieldType.MULTI_SELECT) {
      if (field.options) {
        property.options = field.options.map(opt => ({
          ...opt,
          label: translateFieldString(t || null, opt.label)
        }));
      }
      if (typeof field.allowCustomOptions === 'boolean') {
        property.allowCustomOptions = field.allowCustomOptions;
      }
    }

    // Add placeholder if available
    if (field.placeholder) {
      property.ui.placeholder = translateFieldString(t || null, field.placeholder);
    }

    // Retain namespace for read/write routing
    if (field.namespace) {
      property.namespace = field.namespace;
    }

    // Mark as collapsed by default for lower priority fields
    if (field.priority && field.priority < 50) {
      property.ui.collapsedByDefault = true;
    }

    // Add use case filtering if the section has specific visibility
    // This will be enhanced later when we integrate use case configs

    properties.push(property);
  }

  // Sort by group and then priority
  properties.sort((a, b) => {
    const groupOrder = ['identity', 'life', 'context', 'demographics', 'relationships', 'cultural', 'medical', 'legal', 'financial', 'research', 'extras'];
    const groupA = groupOrder.indexOf(a.ui.group);
    const groupB = groupOrder.indexOf(b.ui.group);

    if (groupA !== groupB) {
      return groupA - groupB;
    }

    return (b.ui.priority || 0) - (a.ui.priority || 0);
  });

  return properties;
}

/**
 * Get the full list of field packs for genogram person
 */
export function getGenogramPersonFieldPacks(): string[] {
  return [
    'genogram-base',
    'genogram-medical-genetic',
    'genogram-legal-estate',
    'genogram-social-work',
    'genogram-family-therapy',
    'genogram-general-research',
  ];
}
